import PrimaryButton from "Components/atoms/buttons/primary-button";
import ResetPasswordFields, { ResetPasswordFormValidation } from "Components/organisms/forms/reset-password/types";
import ProfileTemplate from "Components/templates/profile";
import { FormikHelpers, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { ProfileSliceState, setEmployeeWillingToRelocate } from "slices/auth/profile/reducer";
import { resetPassword, updateWillingToRelocate } from "slices/thunk";
import useManual from "hooks/useManual";
import { getMyContinuingEducationBudget } from "slices/forms/continuing-education/thunk";
import ContinuingEducationBudgetSummary from "models/continuing-education-budget";

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({
        employee,
        jobTitle,
        department,

    }: ProfileSliceState) => ({
        employee,
        jobTitle,
        department
    })
)

const ProfilePage = () => {
    document.title = "User Profile | IbexIQ";
    const { handleOpenManual } = useManual("general/profile");

    const { employee, jobTitle, department } = useSelector(selectProfile)
    const dispatch = useDispatch() as any;
    const [activeTab, setActiveTab] = useState("Security");
    const [isEditing, setIsEditing] = useState(false);
    const [willingToRelocate, setWillingToRelocate] = useState(false);
    const [ceBudget, setCeBudget] = useState<ContinuingEducationBudgetSummary | null>(null);

    useEffect(() => {
        if (!employee) return;
        setWillingToRelocate(employee.willingToRelocate);
    }, [employee]);

    useEffect(() => {
        getMyContinuingEducationBudget().then(setCeBudget);
    }, []);

    const handleEdit = () => {
        if (!employee) return;
        setWillingToRelocate(employee.willingToRelocate);
        setIsEditing(true);
    }

    const handleCancel = () => {
        if (!employee) return;
        setWillingToRelocate(employee.willingToRelocate);
        setIsEditing(false);
    }

    const handleSave = async () => {
        const ok = await updateWillingToRelocate(willingToRelocate);
        if (!ok) return;

        dispatch(setEmployeeWillingToRelocate(willingToRelocate));
        setIsEditing(false);
    }

    const handleResetPassword = async (values: ResetPasswordFields, helpers: FormikHelpers<ResetPasswordFields>) => {
        helpers.setSubmitting(true);
        const res = await resetPassword(values);
        helpers.setSubmitting(false);
        res && helpers.resetForm();
    }

    const {
        values,
        errors,
        touched,
        isValid,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit
    } = useFormik({
        onSubmit: handleResetPassword,
        initialValues: {
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        },
        validationSchema: ResetPasswordFormValidation
    });

    return <ProfileTemplate
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        employee={employee}
        jobTitle={jobTitle}
        department={department}
        isEditing={isEditing}
        willingToRelocate={willingToRelocate}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        handleOpenHelpDocument={handleOpenManual}
        onChangeWillingToRelocate={setWillingToRelocate}
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleSubmit={handleSubmit}
        isValid={isValid}
        isSubmitting={isSubmitting}
        ceBudget={ceBudget}
    />
}

export default ProfilePage;